<template>
  <div class="relative flex flex-col w-full" ref="componentRef">

    <div class="relative"
    :class="{
      'flex-row': currentPage === 'search',
      'flex-col': currentPage !== 'search'
    }"
    >
      <!-- Departure Section -->
      <div v-if="!hideDeparture" ref="departureInputContainer" class="relative w-full cursor-text" @click="focusDepartureInput">
        <div
          :class="[
            'flex items-center border rounded-lg',
            props.compact ? 'h-[50px] px-2 bg-white border-gray-200' : 'sm:px-2 sm:py-1.5 px-1 py-1 bg-white/80 border-white/60',
            {
              'border-2 border-red-500 ring-1 ring-red-300': props.isError,
              'hover:border-blue-400': selectedDepartureItems.length === 0
            }
          ]"
        >
          <!-- Pinned label -->
          <label
            for="departure"
            class="flex-shrink-0 flex items-center px-2 py-1 rounded-md shadow-sm text-xs font-bold text-white sm:text-sm mr-1"
            :class="departureLabelClass"
          >
            {{ departureLabel }}
          </label>

          <!-- Scrollable chip + input strip -->
          <div
            ref="departureChipStrip"
            class="flex flex-nowrap items-center gap-1 flex-1 overflow-x-auto overflow-y-hidden min-w-[140px] sm:min-w-[180px]"
            style="scrollbar-width: none; -ms-overflow-style: none;"
          >
            <div
              v-for="(item, index) in selectedDepartureItems"
              :key="'departure-' + index"
              class="flex-shrink-0 flex items-center px-2 py-1 rounded-md shadow-sm text-xs sm:text-sm transition-all cursor-pointer active:scale-95"
              :class="[departureChipClass, departureFocusedIndex === index ? 'border-2 border-blue-400' : activeDepartureChipCode === item.code ? 'border-2 border-blue-500 scale-105 shadow-md' : 'border-2 border-black/10']"
              @click.stop="onDepartureChipClick(item)"
            >
              {{ item.display }}
              <button
                @click.stop="removeSelectedItem(item, 'departure')"
                class="ml-1 text-blue-500 hover:text-blue-700 focus:outline-none"
                aria-label="Remove departure selection"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <!-- Departure Input -->
            <input
              ref="departureRef"
              type="text"
              id="departure"
              class="py-1 bg-transparent text-xs sm:text-sm text-gray-700 placeholder-gray-400 focus:outline-none min-w-[90px] w-full"
              autocomplete="off"
              :placeholder="selectedDepartureItems.length === 0 ? t('searchForm.departures') : t('searchForm.addMore')"
              v-model="departureQuery"
              @input="debouncedFetchDepartureSuggestions"
              @focus="handleDepartureFocus"
              @keydown.enter.prevent="handleDepartureEnter"
              @keydown.tab="hideDepartureDropdown"
              @keydown.backspace="handleDepartureBackspace"
              @keydown.left="handleDepartureArrowLeft"
              @keydown.right="handleDepartureArrowRight"
              @keydown.delete="handleDepartureDelete"
            />
          </div>

          <!-- Pinned + button -->
          <button
            type="button"
            class="flex-shrink-0 ml-1 flex items-center justify-center w-6 h-6 rounded-full text-blue-500 hover:bg-blue-100 hover:text-blue-700 transition-colors"
            title="Quick pick"
            @click.stop="toggleDepartureQuickPick"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        <!-- Departure Dropdown -->
        <Teleport to="body">
          <transition name="fade">
            <div
              ref="departureDropdownRef"
              v-if="showDepartureDropdown && (departureSuggestions.countries.length || departureSuggestions.cities.length || showNoDepartureSuggestions)"
              class="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 overflow-hidden max-h-72 overflow-y-auto"
              :style="{
                top: departureDropdownPosition.top,
                left: departureDropdownPosition.left,
                width: departureDropdownPosition.width
              }"
              @mousedown.stop
            >
            <template v-if="departureSuggestions.countries.length || departureSuggestions.cities.length">
              <!-- Countries Section -->
              <div v-if="departureSuggestions.countries.length" class="px-3 pt-2.5 pb-1">
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">{{ t('searchForm.countries') }}</p>
              </div>
              <div
                v-for="country in departureSuggestions.countries"
                :key="'departure-country-' + country.id"
                class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm text-gray-700 font-medium"
                @click="setDeparture(country, 'country')"
                role="button" tabindex="0" @keydown.enter="setDeparture(country, 'country')"
              >
                <span class="w-5 h-5 flex items-center justify-center rounded-full bg-blue-50 text-blue-400 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </span>
                {{ translateCountryCode(country.country_code, country.name) }}
              </div>

              <!-- Cities Section -->
              <div v-if="departureSuggestions.cities.length" class="px-3 pt-2 pb-1">
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">{{ t('searchForm.cities') }}</p>
              </div>
              <div v-for="city in departureSuggestions.cities" :key="'departure-city-' + city.id">
                <div
                  class="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                  :class="{ 'bg-gray-50': city.showAirports }"
                  @click="setDeparture(city, 'city')"
                  role="button" tabindex="0" @keydown.enter="setDeparture(city, 'city')"
                >
                  <span class="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </span>
                  <span class="font-medium">{{ cityName(city, locale) }}</span>
                  <span class="text-xs text-gray-400">{{ translateCountryCode(city.country_code, city.country) }}</span>
                  <span v-if="city.airports.length" class="ml-auto" @click.stop="toggleCityAirports(city)" role="button" tabindex="0" @keydown.enter="toggleCityAirports(city)">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" :d="city.showAirports ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" /></svg>
                  </span>
                </div>
                <div v-if="city.showAirports" class="pl-10 pr-3 pb-1">
                  <div
                    v-for="airport in city.airports" :key="'departure-airport-' + airport.id"
                    class="flex items-center gap-1.5 py-1.5 px-2 text-xs text-gray-600 cursor-pointer hover:bg-gray-50 rounded-md"
                    @click="setDeparture(airport, 'airport')" role="button" tabindex="0" @keydown.enter="setDeparture(airport, 'airport')"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                    {{ airportName(airport, locale) }} <span class="text-gray-400">({{ airport.airport_code }})</span>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="showNoDepartureSuggestions">
              <div class="px-4 py-3 text-sm text-gray-400 text-center">No suggestions found.</div>
            </template>
          </div>
        </transition>
      </Teleport>

        <!-- Departure QuickPick -->
        <QuickPickDropdown
          :visible="showDepartureQuickPick && !showDepartureDropdown"
          mode="from"
          :loading="departureQuickPickLoading"
          :showLocateMe="departureCityCount === 0"
          :cities="departureQuickPickCities"
          :showRadius="departureHasCity"
          :radius="departureRadius"
          :useMiles="useMiles"
          :selectedCodes="selectedCityCodes(selectedDepartureItems)"
          @update:radius="onDepartureRadiusChange"
          @select-item="handleDepartureQuickPick"
          @locate-me="handleLocateMe"
          @add-all-done="showDepartureQuickPick = false; scrollChipStripToEnd(departureChipStrip)"
          class="absolute top-full left-0 w-full z-50"
        />
      </div>

      <!-- Destination Section (Similar Structure to Departure) -->
      <div v-if="!hideDestination" ref="destinationInputContainer" class="relative w-full cursor-text" :class="{ 'mt-1': !hideDeparture }" @click="focusDestinationInput"
      >
        <div
          :class="[
            'flex items-center border rounded-lg',
            props.compact ? 'h-[50px] px-2 bg-white border-gray-200' : 'sm:px-2 sm:py-1.5 px-1 py-1 bg-white/80 border-white/60',
            {
              'border-2 border-blue-600 animate-pulse-once': props.highlightTo,
              'hover:border-green-500': selectedDestinationItems.length === 0
            }
          ]"
        >
          <!-- Pinned label -->
          <label for="destination" class="flex-shrink-0 flex items-center px-2 py-1 bg-green-700 rounded-md shadow-sm text-xs font-bold text-white sm:text-sm mr-1">
            {{ destinationLabel }}
          </label>

          <!-- Scrollable chip + input strip -->
          <div
            ref="destinationChipStrip"
            class="flex flex-nowrap items-center gap-1 flex-1 overflow-x-auto overflow-y-hidden min-w-[140px] sm:min-w-[180px]"
            style="scrollbar-width: none; -ms-overflow-style: none;"
          >
            <div
              v-for="(item, index) in selectedDestinationItems"
              :key="'destination-' + index"
              class="flex-shrink-0 flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-md shadow-sm text-xs sm:text-sm transition-all cursor-pointer active:scale-95"
              :class="destinationFocusedIndex === index ? 'border-2 border-green-400' : activeDestinationChipCode === item.code ? 'border-2 border-green-500 scale-105 shadow-md' : 'border-2 border-black/10'"
              @click.stop="onDestinationChipClick(item)"
            >
              {{ item.display }}
              <button
                @click.stop="removeSelectedItem(item, 'destination')"
                class="ml-1 text-green-500 hover:text-green-700 focus:outline-none"
                aria-label="Remove destination selection"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div
              v-if="isVisible && selectedDestinationItems.length === 0 && !destinationQuery"
              class="flex-shrink-0 flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-md border-2 border-black/10 shadow-sm text-xs sm:text-sm cursor-pointer"
              @click.stop="toggleDestinationQuickPick"
            >
              {{ t('searchForm.defaultAnywhere') }}
              <button
                @click.stop="isVisible = false"
                class="ml-1 text-green-500 hover:text-green-700 focus:outline-none"
                aria-label="Remove destination selection"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <!-- Destination Input -->
            <input
              ref="destinationRef"
              type="text"
              id="destination"
              class="py-1 bg-transparent text-xs sm:text-sm text-gray-700 placeholder-gray-400 focus:outline-none min-w-[90px] w-full"
              autocomplete="off"
              :placeholder="selectedDestinationItems.length === 0 ? (isVisible ? t('searchForm.addDestination') : t('searchForm.destinations')) : t('searchForm.addMore')"
              v-model="destinationQuery"
              @input="debouncedFetchDestinationSuggestions"
              @focus="handleDestinationFocus"
              @keydown.enter.prevent="handleDestinationEnter"
              @keydown.tab="hideDestinationDropdown"
              @keydown.backspace="handleDestinationBackspace"
              @keydown.left="handleDestinationArrowLeft"
              @keydown.right="handleDestinationArrowRight"
              @keydown.delete="handleDestinationDelete"
            />
          </div>

          <!-- Pinned + button -->
          <button
            type="button"
            class="flex-shrink-0 ml-1 flex items-center justify-center w-6 h-6 rounded-full text-green-500 hover:bg-green-100 hover:text-green-700 transition-colors"
            title="Quick pick"
            @click.stop="toggleDestinationQuickPick"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <!-- Map Component -->
          <Teleport to="body">
            <transition name="map-sheet">
              <div
                v-if="isMapVisible"
                class="fixed inset-0 z-[9999] flex flex-col justify-end md:justify-center md:items-center"
              >
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/50" @click="toggleMapVisibility"></div>

                <!-- Sheet / Modal -->
                <div class="relative w-full md:w-[680px] max-h-[90vh] md:max-h-[85vh] bg-white md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden">
                  <!-- Drag handle (mobile only) -->
                  <div class="flex justify-center pt-2 pb-1 md:hidden">
                    <div class="w-10 h-1 rounded-full bg-gray-300"></div>
                  </div>

                  <!-- Map content -->
                  <div class="pt-1 pb-1 overflow-y-auto flex-1 min-h-0">
                    <MapComponent />
                  </div>

                  <!-- Close button at bottom -->
                  <div class="px-4 pb-4 pt-1">
                    <button
                      @click="toggleMapVisibility"
                      type="button"
                      class="w-full py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700 transition-colors"
                    >
                      Close map
                    </button>
                  </div>
                </div>
              </div>
            </transition>
          </Teleport>
        </div>

        <!-- Destination Dropdown -->
        <Teleport to="body">
          <transition name="fade">
            <div
              ref="destinationDropdownRef"
              v-if="showDestinationDropdown && (destinationSuggestions.countries.length || destinationSuggestions.cities.length || showNoDestinationSuggestions)"
              class="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 overflow-hidden max-h-72 overflow-y-auto"
              :style="{
                top: destinationDropdownPosition.top,
                left: destinationDropdownPosition.left,
                width: destinationDropdownPosition.width
              }"
              @mousedown.stop
            >
            <template v-if="destinationSuggestions.countries.length || destinationSuggestions.cities.length">
              <!-- Countries Section -->
              <div v-if="destinationSuggestions.countries.length" class="px-3 pt-2.5 pb-1">
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">{{ t('searchForm.countries') }}</p>
              </div>
              <div
                v-for="country in destinationSuggestions.countries"
                :key="'destination-country-' + country.id"
                class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm text-gray-700 font-medium"
                @click="setDestination(country, 'country')"
                role="button" tabindex="0" @keydown.enter="setDestination(country, 'country')"
              >
                <span class="w-5 h-5 flex items-center justify-center rounded-full bg-green-50 text-green-400 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </span>
                {{ translateCountryCode(country.country_code, country.name) }}
              </div>

              <!-- Cities Section -->
              <div v-if="destinationSuggestions.cities.length" class="px-3 pt-2 pb-1">
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">{{ t('searchForm.cities') }}</p>
              </div>
              <div v-for="city in destinationSuggestions.cities" :key="'destination-city-' + city.id">
                <div
                  class="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                  :class="{ 'bg-gray-50': city.showAirports }"
                  @click="setDestination(city, 'city')"
                  role="button" tabindex="0" @keydown.enter="setDestination(city, 'city')"
                >
                  <span class="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </span>
                  <span class="font-medium">{{ cityName(city, locale) }}</span>
                  <span class="text-xs text-gray-400">{{ translateCountryCode(city.country_code, city.country) }}</span>
                  <span v-if="city.airports.length" class="ml-auto" @click.stop="toggleCityAirports(city)" role="button" tabindex="0" @keydown.enter="toggleCityAirports(city)">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" :d="city.showAirports ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" /></svg>
                  </span>
                </div>
                <div v-if="city.showAirports" class="pl-10 pr-3 pb-1">
                  <div
                    v-for="airport in city.airports" :key="'destination-airport-' + airport.id"
                    class="flex items-center gap-1.5 py-1.5 px-2 text-xs text-gray-600 cursor-pointer hover:bg-gray-50 rounded-md"
                    @click="setDestination(airport, 'airport')" role="button" tabindex="0" @keydown.enter="setDestination(airport, 'airport')"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                    {{ airportName(airport, locale) }} <span class="text-gray-400">({{ airport.airport_code }})</span>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="showNoDestinationSuggestions">
              <div class="px-4 py-3 text-sm text-gray-400 text-center">No suggestions found.</div>
            </template>
          </div>
        </transition>
      </Teleport>

        <!-- Destination QuickPick -->
        <QuickPickDropdown
          :visible="showDestinationQuickPick && !showDestinationDropdown"
          mode="to"
          :loading="destinationQuickPickLoading"
          :cities="destinationQuickPickCities"
          :continents="destinationCityCount === 0 ? destinationContinents : []"
          :showRadius="destinationHasCity"
          :radius="destinationRadius"
          :useMiles="useMiles"
          :selectedCodes="selectedCityCodes(selectedDestinationItems)"
          @update:radius="onDestinationRadiusChange"
          @select-item="handleDestinationQuickPick"
          @open-map="handleQuickPickOpenMap"
          @add-all-done="showDestinationQuickPick = false; scrollChipStripToEnd(destinationChipStrip)"
          class="absolute top-full left-0 w-full z-50"
        />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted, onMounted, watch, computed, nextTick } from 'vue';
import axios from '@/axios'; // Ensure the path is correct
import { proposedOriginCode, trackOriginAutofill, trackOriginChanged } from '@/utils/originAnalytics';
import { debounce } from '@/utils/debounce';
import MapComponent from "@/components/MapComponent.vue";
import QuickPickDropdown from "@/components/SearchComponents/QuickPickDropdown.vue";
import { usePermissionStore } from '@/stores/permission';
import eventBus from '@/eventBus'
import { useScreenSize } from '@/composables/useScreenSize';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { cityName, airportName } from '@/utils/cityName';
import { getAutofillOrigin } from '@/utils/locationResolver';

const route = useRoute();

const { isLargeScreen } = useScreenSize();
const { locale, t } = useI18n();

const translateCountryCode = (code, fallback) => {
  const dn = new Intl.DisplayNames([locale.value === 'pl' ? 'pl' : 'en'], { type: 'region' });
  try {
    if (code) { const r = dn.of(code.toUpperCase()); if (r) return r; }
    if (fallback && /^[A-Z]{1,3}$/.test(fallback)) { const r = dn.of(fallback); if (r) return r; }
  } catch { /* fall through */ }
  return fallback || '';
};

const currentPage = ref(route.name || 'search')

console.log('DD isLargeScreen.valasdasdasdasdue', isLargeScreen.value);


import { useAuthStore } from '@/stores/authStore';  // Add auth store
const authStore = useAuthStore();  // Add auth store
console.log('DD authStore.valasdasdasdasdue', authStore.isAuthenticated);

// Define emits correctly as an array
const emit = defineEmits(['update-departureCities', 'update-destinationCities']);

// Define props
const props = defineProps({
  isError: {
    type: Boolean,
    default: false,
  },
  departureCities: {
    type: Array,
    default: () => []
  },
  destinationCities: {
    type: Array,
    default: () => []
  },
  highlightTo: {
    type: Boolean,
    default: false,
  },
  focusElement: {
    type: String,
    default: ''
  },
  hideDestination: {
    type: Boolean,
    default: false
  },
  hideDeparture: {
    type: Boolean,
    default: false
  },
  departureLabel: {
    type: String,
    default: 'From:'
  },
  destinationLabel: {
    type: String,
    default: 'To:'
  },
  departureLabelClass: {
    type: String,
    default: 'bg-blue-600'
  },
  skipAutoFill: {
    type: Boolean,
    default: false
  },
  departureChipClass: {
    type: String,
    default: 'bg-blue-100 text-blue-700'
  },
  compact: {
    type: Boolean,
    default: false
  }
});

// References to component and input fields
const componentRef = ref(null);
const departureRef = ref(null);
const destinationRef = ref(null);
const departureChipStrip = ref(null);
const destinationChipStrip = ref(null);

// Refs for dropdown positioning
const departureInputContainer = ref(null);
const destinationInputContainer = ref(null);
const departureDropdownRef = ref(null);
const destinationDropdownRef = ref(null);

// Position styles for teleported dropdowns
const departureDropdownPosition = ref({ top: '0px', left: '0px', width: '0px' });
const destinationDropdownPosition = ref({ top: '0px', left: '0px', width: '0px' });

// Function to update dropdown positions (fixed positioning — viewport-relative)
const updateDropdownPositions = () => {
  if (departureInputContainer.value && showDepartureDropdown.value) {
    const rect = departureInputContainer.value.getBoundingClientRect();
    departureDropdownPosition.value = {
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`
    };
  }

  if (destinationInputContainer.value && showDestinationDropdown.value) {
    const rect = destinationInputContainer.value.getBoundingClientRect();
    destinationDropdownPosition.value = {
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`
    };
  }
};

const scrollChipStripToEnd = (el) => {
  nextTick(() => { if (el) el.scrollLeft = el.scrollWidth; });
};
const isVisible = ref(true);

// Dropdown visibility flags
const showDepartureDropdown = ref(false);
const showDestinationDropdown = ref(false);

// Watch for dropdown visibility changes to update positions
watch(showDepartureDropdown, (isVisible) => {
  if (isVisible) {
    nextTick(updateDropdownPositions);
  }
});

watch(showDestinationDropdown, (isVisible) => {
  if (isVisible) {
    nextTick(updateDropdownPositions);
  }
});

// Track which chip the user clicked to show nearby for
const activeDepartureChipCode = ref(null);
const activeDestinationChipCode = ref(null);

// ── QuickPick state ──────────────────────────────────────────────────────
const permissionStore = usePermissionStore();

const showDepartureQuickPick = ref(false);
const showDestinationQuickPick = ref(false);
const departureQuickPickLoading = ref(false);
const destinationQuickPickLoading = ref(false);
// Epoch counters: increment before every fetch; stale responses are discarded.
const departureNearbyEpoch = ref(0);
const destinationNearbyEpoch = ref(0);
const departureQuickPickCities = ref([]);
const destinationQuickPickCities = ref([]);

const destinationContinents = [
  { code: 'EU', name: 'Europe' },
  { code: 'AS', name: 'Asia' },
  { code: 'NA', name: 'North America' },
  { code: 'SA', name: 'South America' },
  { code: 'AF', name: 'Africa' },
  { code: 'OC', name: 'Oceania' },
];

// Radius sliders (100-400km). Nearby lookups recompute instantly on the backend.
const departureRadius = ref(150);
const destinationRadius = ref(150);

// Show distances in miles for US and UK users (detected from stored country code).
const MILES_COUNTRIES = new Set(['US', 'GB', 'LR', 'MM']);
const useMiles = computed(() => MILES_COUNTRIES.has((localStorage.getItem('countryCode') || '').toUpperCase()));

// Collect the city_codes of every selected CITY (ignores countries/continents).
const selectedCityCodes = (items) =>
  items.filter(i => i.type === 'city' && i.code).map(i => i.code);

// Number of actual cities selected (countries/continents don't count for nearby lookups).
const departureCityCount = computed(() => selectedCityCodes(selectedDepartureItems.value).length);
const destinationCityCount = computed(() => selectedCityCodes(selectedDestinationItems.value).length);

// Show the radius slider whenever at least one city is selected.
const departureHasCity = computed(() => departureCityCount.value >= 1);
const destinationHasCity = computed(() => destinationCityCount.value >= 1);

const NEARBY_MAX_SELECTED = 20; // skip nearby lookups beyond this many cities

// Merge two nearby-cities result arrays, keeping the minimum distance per city code.
// excludeCodes filters out cities the user already selected.
const mergeNearby = (primary, secondary, excludeCodes = []) => {
  const exclude = new Set(excludeCodes);
  const map = new Map();
  for (const c of primary) { if (!exclude.has(c.code)) map.set(c.code, { ...c }); }
  for (const c of secondary) {
    if (exclude.has(c.code)) continue;
    const existing = map.get(c.code);
    if (!existing || c.distance_km < existing.distance_km) map.set(c.code, { ...c });
  }
  return [...map.values()].sort((a, b) => (a.distance_km || 0) - (b.distance_km || 0));
};

const fetchDepartureNearby = async () => {
  const epoch = ++departureNearbyEpoch.value;
  const codes = selectedCityCodes(selectedDepartureItems.value);
  if (codes.length === 0) {
    departureQuickPickCities.value = (permissionStore.nearbyCities || []).map(c => ({
      name: c.name, code: c.code, country: '', country_code: c.country_code || '',
    }));
    return;
  }
  if (codes.length > NEARBY_MAX_SELECTED) { departureQuickPickCities.value = []; return; }
  departureQuickPickLoading.value = true;
  try {
    const primaryRes = await axios.get('/search/nearby-cities/', {
      params: { city_codes: codes[0], radius_km: departureRadius.value, limit: 20 },
    });
    let results = primaryRes.data.cities || [];
    if (codes.length > 1) {
      const secondaryRes = await axios.get('/search/nearby-cities/', {
        params: { city_codes: codes.slice(1).join(','), radius_km: 100, limit: 20 },
      });
      results = mergeNearby(results, secondaryRes.data.cities || [], codes);
    }
    if (epoch === departureNearbyEpoch.value) departureQuickPickCities.value = results.slice(0, 20);
  } catch { if (epoch === departureNearbyEpoch.value) departureQuickPickCities.value = []; }
  if (epoch === departureNearbyEpoch.value) departureQuickPickLoading.value = false;
};

const fetchDestinationNearby = async () => {
  const epoch = ++destinationNearbyEpoch.value;
  const codes = selectedCityCodes(selectedDestinationItems.value);
  if (codes.length === 0) {
    destinationQuickPickCities.value = [];
    return;
  }
  if (codes.length > NEARBY_MAX_SELECTED) { destinationQuickPickCities.value = []; return; }
  destinationQuickPickLoading.value = true;
  try {
    const primaryRes = await axios.get('/search/nearby-cities/', {
      params: { city_codes: codes[0], radius_km: destinationRadius.value, limit: 20 },
    });
    let results = primaryRes.data.cities || [];
    if (codes.length > 1) {
      const secondaryRes = await axios.get('/search/nearby-cities/', {
        params: { city_codes: codes.slice(1).join(','), radius_km: 100, limit: 20 },
      });
      results = mergeNearby(results, secondaryRes.data.cities || [], codes);
    }
    if (epoch === destinationNearbyEpoch.value) destinationQuickPickCities.value = results.slice(0, 20);
  } catch { if (epoch === destinationNearbyEpoch.value) destinationQuickPickCities.value = []; }
  if (epoch === destinationNearbyEpoch.value) destinationQuickPickLoading.value = false;
};

const toggleDepartureQuickPick = async () => {
  showDestinationQuickPick.value = false;
  showDepartureQuickPick.value = !showDepartureQuickPick.value;
  if (!showDepartureQuickPick.value) return;
  await fetchDepartureNearby();
};

const toggleDestinationQuickPick = async () => {
  showDepartureQuickPick.value = false;
  showDestinationQuickPick.value = !showDestinationQuickPick.value;
  if (!showDestinationQuickPick.value) return;
  await fetchDestinationNearby();
};

// Fetch nearby cities scoped to a single reference city code (used for chip-click view).
const fetchDepartureNearbyForCode = async (code, radius) => {
  const epoch = ++departureNearbyEpoch.value;
  departureQuickPickLoading.value = true;
  try {
    const res = await axios.get('/search/nearby-cities/', {
      params: { city_codes: code, radius_km: radius, limit: 20 },
    });
    if (epoch === departureNearbyEpoch.value) departureQuickPickCities.value = res.data.cities || [];
  } catch { if (epoch === departureNearbyEpoch.value) departureQuickPickCities.value = []; }
  if (epoch === departureNearbyEpoch.value) departureQuickPickLoading.value = false;
};
const fetchDestinationNearbyForCode = async (code, radius) => {
  const epoch = ++destinationNearbyEpoch.value;
  destinationQuickPickLoading.value = true;
  try {
    const res = await axios.get('/search/nearby-cities/', {
      params: { city_codes: code, radius_km: radius, limit: 20 },
    });
    if (epoch === destinationNearbyEpoch.value) destinationQuickPickCities.value = res.data.cities || [];
  } catch { if (epoch === destinationNearbyEpoch.value) destinationQuickPickCities.value = []; }
  if (epoch === destinationNearbyEpoch.value) destinationQuickPickLoading.value = false;
};

// Click on an already-selected chip → open QuickPick showing nearby for that city at 200km.
const onDepartureChipClick = async (item) => {
  if (item.type !== 'city' || !item.code) return;
  showDestinationQuickPick.value = false;
  activeDestinationChipCode.value = null;
  activeDepartureChipCode.value = item.code;
  departureRadius.value = 200;
  showDepartureQuickPick.value = true;
  await fetchDepartureNearbyForCode(item.code, 200);
};

const onDestinationChipClick = async (item) => {
  if (item.type !== 'city' || !item.code) return;
  showDepartureQuickPick.value = false;
  activeDepartureChipCode.value = null;
  activeDestinationChipCode.value = item.code;
  destinationRadius.value = 200;
  showDestinationQuickPick.value = true;
  await fetchDestinationNearbyForCode(item.code, 200);
};

// Re-fetch (instant, DB-filtered) whenever the radius slider changes.
// If a chip is actively focused (chip-click scoped view), keep the fetch scoped to
// that city instead of silently reverting to the generic multi-city aggregate.
const onDepartureRadiusChange = (val) => {
  departureRadius.value = val;
  if (activeDepartureChipCode.value) {
    fetchDepartureNearbyForCode(activeDepartureChipCode.value, val);
  } else {
    fetchDepartureNearby();
  }
};
const onDestinationRadiusChange = (val) => {
  destinationRadius.value = val;
  if (activeDestinationChipCode.value) {
    fetchDestinationNearbyForCode(activeDestinationChipCode.value, val);
  } else {
    fetchDestinationNearby();
  }
};

const handleDepartureQuickPick = (item) => {
  if (item.type === 'city') {
    // Multiselect: toggle without closing dropdown
    const exists = selectedDepartureItems.value.some(s => s.id === item.id && s.type === 'city');
    if (exists) {
      removeSelectedItem({ id: item.id, type: item.type, code: item.code }, 'departure');
    } else {
      setDeparture({ id: item.id, name: item.display, city_code: item.code }, item.type);
    }
  } else {
    showDepartureQuickPick.value = false;
    setDeparture({ id: item.id, name: item.display, city_code: item.code }, item.type);
  }
};

const handleDestinationQuickPick = (item) => {
  if (item.type === 'city') {
    // Multiselect: toggle without closing dropdown
    const exists = selectedDestinationItems.value.some(s => s.id === item.id && s.type === 'city');
    if (exists) {
      removeSelectedItem({ id: item.id, type: item.type, code: item.code }, 'destination');
    } else {
      setDestination({ id: item.id, name: item.display, city_code: item.code, country_code: item.code }, item.type);
    }
  } else {
    showDestinationQuickPick.value = false;
    setDestination({ id: item.id, name: item.display, city_code: item.code, country_code: item.code }, item.type);
  }
};

const handleLocateMe = async () => {
  departureQuickPickLoading.value = true;
  try {
    const success = await permissionStore.refreshTeamUserLocation();
    if (success) {
      // Wait a bit for the GPS callback to resolve
      await new Promise(r => setTimeout(r, 2000));
      departureQuickPickCities.value = (permissionStore.nearbyCities || []).map(c => ({
        name: c.name, code: c.code, country: '', country_code: c.country_code || '',
      }));
      // Auto-select the detected city
      if (permissionStore.cityCode && permissionStore.cityName) {
        setDeparture({ id: permissionStore.cityCode, name: permissionStore.cityName, city_code: permissionStore.cityCode }, 'city');
      }
    }
  } catch (e) { console.error('Locate me failed:', e); }
  departureQuickPickLoading.value = false;
};

const handleQuickPickOpenMap = () => {
  showDestinationQuickPick.value = false;
  toggleMapVisibility();
};

// Map visibility
const isMapVisible = ref(false);
const toggleMapVisibility = () => {
  isMapVisible.value = !isMapVisible.value;
  console.log('isMapVisible.value', isMapVisible.value);
};

function syncSelectionsToMap() {
  if (!window.simplemaps_select?.select) return;
  selectedDestinationItems.value.forEach(item => {
    if (item.type === 'continent' && window.simplemaps_worldmap_mapdata) {
      const regionIndex = continentToRegionMap[item.code];
      if (regionIndex !== undefined) {
        window.simplemaps_worldmap_mapdata.regions[regionIndex].states.forEach(
          stateId => window.simplemaps_select.select(stateId)
        );
        eventBus.emit('continent-selected-from-field', item.code);
      }
    } else if (item.type === 'country' && item.code) {
      // eslint-disable-next-line no-empty
      try { window.simplemaps_select.select(item.code); } catch (e) {}
    }
  });
}

watch(isMapVisible, (visible) => {
  if (!visible) return;
  const syncInterval = setInterval(() => {
    if (window.simplemaps_select?.select) {
      clearInterval(syncInterval);
      syncSelectionsToMap();
    }
  }, 50);
  setTimeout(() => clearInterval(syncInterval), 5000);
});

// Suggestions lists
const departureSuggestions = ref({
  countries: [],
  cities: [],
});
const destinationSuggestions = ref({
  countries: [],
  cities: [],
});

const focusDestinationInput = (event) => {
  // Check if the click was on a button or input to prevent double focus
  if (
    event.target.tagName !== 'BUTTON' &&
    event.target.tagName !== 'INPUT' &&
    !event.target.closest('button')
  ) {
    destinationRef.value.focus();
  }
};

const focusDepartureInput = (event) => {
  // Check if the click was on a button or input to prevent double focus
  if (
    event.target.tagName !== 'BUTTON' &&
    event.target.tagName !== 'INPUT' &&
    !event.target.closest('button')
  ) {
    departureRef.value.focus();
  }
};

// Selected items
const selectedDepartureItems = ref([]); // Multiple selections (departure)
const selectedDestinationItems = ref([]); // Multiple selections (destination)
const isAnywhereItem = item => [item?.code, item?.id, item?.display, item?.name, item?.type]
  .some(value => String(value || '').trim().toLowerCase() === 'anywhere');

// Multiselect keeps the quick pick open, so refresh the nearby list whenever the
// number of selected cities changes (it is only shown for exactly one city).
// NOTE: must stay below the refs above — watch() eagerly evaluates its source.
watch(showDepartureQuickPick, (v) => { if (!v) activeDepartureChipCode.value = null; });
watch(showDestinationQuickPick, (v) => { if (!v) activeDestinationChipCode.value = null; });
watch(departureCityCount, () => {
  if (showDepartureQuickPick.value) {
    if (activeDepartureChipCode.value) {
      // Stay in chip-scoped view — just refresh so newly-added cities show as checked.
      fetchDepartureNearbyForCode(activeDepartureChipCode.value, departureRadius.value);
    } else {
      fetchDepartureNearby();
    }
  }
});
watch(destinationCityCount, () => {
  if (showDestinationQuickPick.value) {
    if (activeDestinationChipCode.value) {
      fetchDestinationNearbyForCode(activeDestinationChipCode.value, destinationRadius.value);
    } else {
      fetchDestinationNearby();
    }
  }
});

// Flag to prevent prop watcher from overwriting internal updates
const isInternalUpdate = ref(false);

// Keyboard navigation - track which item is focused
const departureFocusedIndex = ref(-1); // -1 means input is focused, >= 0 means item is focused
const destinationFocusedIndex = ref(-1);

// Flags to show "No suggestions found."
const showNoDepartureSuggestions = ref(false);
const showNoDestinationSuggestions = ref(false);

// Query inputs
const departureQuery = ref('');
const destinationQuery = ref('');

// Error messages (optional enhancement)
const errorMessage = ref('');

// Function to set departure item and emit updated CodesCSV
function setDeparture(item, type) {
  // Check if item already exists to prevent duplicates
  const code = (item.airport_code || item.city_code || item.country_code || item.code || '').replace(/-/g, '').toUpperCase();
  const exists = selectedDepartureItems.value.some(
    selected => selected.type === type && String(selected.code || '').toUpperCase() === code
  );
  if (exists || !code) return;

  // Add new selection (allow multiple departures)
  const newItem = {
    id: item.id,
    display: type === 'airport' ? (airportName(item, locale.value) || cityName(item, locale.value)) : cityName(item, locale.value),
    type: type,
    code,
  };
  
  selectedDepartureItems.value.push(newItem);

  // A city typed over the one we proposed is the visitor telling us the guess was
  // wrong; the tracker ignores it when the code is the one it proposed itself.
  trackOriginChanged(code, 'departure-field');

  // Clear input and suggestions
  departureQuery.value = '';
  departureSuggestions.value = { countries: [], cities: [] };
  showDepartureDropdown.value = false;
  
  // Reset focus index so backspace works properly
  departureFocusedIndex.value = -1;

  // Emit updated departureCities as array
  emit('update-departureCities', [...selectedDepartureItems.value]);
  scrollChipStripToEnd(departureChipStrip.value);
}

// Function to set destination item and emit updated CodesCSV
function setDestination(item, type) {
  selectedDestinationItems.value = selectedDestinationItems.value.filter(selected => !isAnywhereItem(selected));
  isVisible.value = false;
  // Prevent duplicates by canonical code, not database id.
  const code = (item.airport_code || item.city_code || item.country_code || item.code || '').replace(/-/g, '').toUpperCase();
  const exists = selectedDestinationItems.value.some(
    selected => selected.type === type && String(selected.code || '').toUpperCase() === code
  );

  if (exists || !code) return;
  console.log('Setting destination:', item);
  // Add new selection
  const newItem = {
    id: item.id,
    display: type === 'airport' ? (airportName(item, locale.value) || cityName(item, locale.value)) : cityName(item, locale.value),
    type: type,
    code,
  };
  
  selectedDestinationItems.value.push(newItem);
  isVisible.value = false;

  console.log('Selected destination items:', selectedDestinationItems.value);

  // Clear input and suggestions
  destinationQuery.value = '';
  destinationSuggestions.value = { countries: [], cities: [] };
  showDestinationDropdown.value = false;
  
  // Reset focus index so backspace works properly
  destinationFocusedIndex.value = -1;

  // Check if the item is a country and get the code from the last added destination item
  if(type === 'country') {
    const lastItem = selectedDestinationItems.value[selectedDestinationItems.value.length - 1];
    if (lastItem && lastItem.code && window.simplemaps_select && window.simplemaps_select.select) {
      console.log('Setting country:', lastItem.code);
      window.simplemaps_select.select(lastItem.code);
    }
  }
  
  // Emit the array of selected destination items
  emit('update-destinationCities', [...selectedDestinationItems.value]);
  scrollChipStripToEnd(destinationChipStrip.value);
}

const continentToRegionMap = {
  'europe': 2, 'asia': 4, 'africa': 3,
  'namerica': 0, 'samerica': 1, 'oceania': 5,
};

function deselectFromMap(item) {
  if (!window.simplemaps_select?.deselect) return;
  if (item.type === 'continent' && window.simplemaps_worldmap_mapdata) {
    const regionIndex = continentToRegionMap[item.code];
    if (regionIndex !== undefined) {
      window.simplemaps_worldmap_mapdata.regions[regionIndex].states.forEach(
        stateId => window.simplemaps_select.deselect(stateId)
      );
      eventBus.emit('continent-deselected-from-field', item.code);
    }
  } else if (item.code) {
    // eslint-disable-next-line no-empty
    try { window.simplemaps_select.deselect(item.code); } catch (e) {}
  }
}

// Function to remove selected item and emit updated CodesCSV
function removeSelectedItem(item, type) {
  if (type === 'departure') {
    // Remove only the specific item, not all items
    selectedDepartureItems.value = selectedDepartureItems.value.filter(
      selected => selected.id !== item.id || selected.type !== item.type
    );
    if (String(item.code || '').toUpperCase() === proposedOriginCode()) {
      trackOriginChanged('', 'departure-field');
    }
    // Reset focused index
    departureFocusedIndex.value = -1;
    // Emit updated departureCities
    emit('update-departureCities', [...selectedDepartureItems.value]);
  } else if (type === 'destination') {
    // Remove the specific item from selectedDestinationItems
    selectedDestinationItems.value = selectedDestinationItems.value.filter(
      selected => selected.id !== item.id
    );

    deselectFromMap(item);

    // Reset focused index
    destinationFocusedIndex.value = -1;
    // Emit updated destinationCities
    emit('update-destinationCities', [...selectedDestinationItems.value]);
  }
}

// Function to check if we have both departure and destination selected and trigger location search if we do
// eslint-disable-next-line no-unused-vars
function checkAndTriggerLocationSearch() {
  // Only trigger search if we have at least one departure and one destination
  if (selectedDepartureItems.value.length > 0 && selectedDestinationItems.value.length > 0) {
    console.log('Both departure and destination selected, triggering location search');
    
    // Get the only departure item (first one)
    const departureItem = selectedDepartureItems.value[0];
    
    // Get the last destination item
    const destinationItem = selectedDestinationItems.value[selectedDestinationItems.value.length - 1];
    
    console.log('Using departure:', departureItem);
    console.log('Using destination:', destinationItem);
    
    // Prepare data with the specific departure and destination items
    const searchData = {
      departure: [departureItem],  // Always use the only departure item
      destination: [destinationItem],  // Always use the last destination item
      selectedDates: {  // Use current date as default
        start: new Date().toISOString(),
        end: new Date().toISOString()
      },
      passengers: 1,
      currency: 'GBP'  // Default currency
    };
    
    // Make an async API call to the backend
    axios.post('/search/location/', {
      location: searchData
    })
      .then(response => {
        console.log('Location search response:', response.data);
      })
      .catch(error => {
        console.error('Error in location search:', error);
      });
  } else {
    console.log('Need both departure and destination to trigger search');
  }
}

// Function to reset destination items
// eslint-disable-next-line no-unused-vars
function resetDestinationItems() {
  selectedDestinationItems.value = [];
  destinationQuery.value = '';
  showDestinationDropdown.value = false;
}

// Function to reset departure items
// eslint-disable-next-line no-unused-vars
function resetDepartureItems() {
  selectedDepartureItems.value = [];
  departureQuery.value = '';
  showDepartureDropdown.value = false;
}

// Debounced fetch functions to limit API calls
const debouncedFetchDepartureSuggestions = debounce(async () => {
  await fetchDepartureSuggestions();
}, 300);

const debouncedFetchDestinationSuggestions = debounce(async () => {
  await fetchDestinationSuggestions();
}, 300);

// Function to fetch departure suggestions
async function fetchDepartureSuggestions() {
  const query = departureQuery.value.trim();
  if (!query) {
    departureSuggestions.value = { countries: [], cities: [] };
    showNoDepartureSuggestions.value = false;
    return;
  }

  try {
    const response = await axios.get('/search/dept_locations/', { params: { q: query } });
    const data = response.data;

    // Ensure data.countries and data.cities are arrays
    const countries = Array.isArray(data.countries) ? data.countries : [];
    const cities = Array.isArray(data.cities) ? data.cities : [];

    departureSuggestions.value = {
      countries: countries.map(country => ({
        id: country.id,
        name: country.name,
        country_code: country.country_code,
      })),
      cities: cities.map(city => ({
        id: city.id,
        name: cityName(city, locale.value),
        city_code: city.city_code,
        country: city.country && city.country.name ? city.country.name : 'Unknown',
        country_code: city.country && city.country.country_code ? city.country.country_code : '',
        airports: Array.isArray(city.airports) ? city.airports : [],
        showAirports: query.length >= 3 && Array.isArray(city.airports) && city.airports.some(a => (a.display_name || a.name) && (a.display_name || a.name).toLowerCase().includes(query.toLowerCase())), // Auto-expand on airport name match
      })),
    };

    showNoDepartureSuggestions.value =
      departureSuggestions.value.countries.length === 0 &&
      departureSuggestions.value.cities.length === 0;
    showDepartureDropdown.value = true;
  } catch (error) {
    console.error('Error fetching departure suggestions:', error);
    errorMessage.value = t('errors.failedLoadDepartureSuggestions');
    showNoDepartureSuggestions.value = true;
    departureSuggestions.value = { countries: [], cities: [] };
    showDepartureDropdown.value = true;
  }
}

// Function to fetch destination suggestions
async function fetchDestinationSuggestions() {
  const query = destinationQuery.value.trim();
  if (!query) {
    destinationSuggestions.value = { countries: [], cities: [] };
    showDestinationDropdown.value = false;
    showNoDestinationSuggestions.value = false;
    return;
  }

  try {
    const response = await axios.get('/search/dest_locations/', { params: { q: query } });
    const data = response.data;

    // Ensure data.countries and data.cities are arrays
    const countries = Array.isArray(data.countries) ? data.countries : [];
    const cities = Array.isArray(data.cities) ? data.cities : [];

    destinationSuggestions.value = {
      countries: countries.map(country => ({
        id: country.id,
        name: country.name,
        country_code: country.country_code,
      })),
      cities: cities.map(city => ({
        id: city.id,
        name: cityName(city, locale.value),
        city_code: city.city_code,
        country: city.country && city.country.name ? city.country.name : 'Unknown',
        country_code: city.country && city.country.country_code ? city.country.country_code : '',
        airports: Array.isArray(city.airports) ? city.airports : [],
        showAirports: query.length >= 3 && Array.isArray(city.airports) && city.airports.some(a => (a.display_name || a.name) && (a.display_name || a.name).toLowerCase().includes(query.toLowerCase())), // Auto-expand on airport name match
      })),
    };

    showNoDestinationSuggestions.value =
      destinationSuggestions.value.countries.length === 0 &&
      destinationSuggestions.value.cities.length === 0;
    showDestinationDropdown.value = true;
  } catch (error) {
    console.error('Error fetching destination suggestions:', error);
    errorMessage.value = t('errors.failedLoadDestinationSuggestions');
    showNoDestinationSuggestions.value = true;
    destinationSuggestions.value = { countries: [], cities: [] };
    showDestinationDropdown.value = true;
  }
}

// Function to toggle airport dropdown visibility for a city
function toggleCityAirports(city) {
  city.showAirports = !city.showAirports;
}

// Handle Enter key press for Departure
function handleDepartureEnter() {
  if (departureSuggestions.value.countries.length > 0) {
    setDeparture(departureSuggestions.value.countries[0], 'country');
  } else if (departureSuggestions.value.cities.length > 0) {
    const firstCity = departureSuggestions.value.cities[0];
    if (firstCity.airports.length > 0) {
      setDeparture(firstCity.airports[0], 'airport');
    } else {
      setDeparture(firstCity, 'city');
    }
  }
}

// Handle Enter key press for Destination
function handleDestinationEnter() {
  if (destinationSuggestions.value.countries.length > 0) {
    setDestination(destinationSuggestions.value.countries[0], 'country');
  } else if (destinationSuggestions.value.cities.length > 0) {
    const firstCity = destinationSuggestions.value.cities[0];
    if (firstCity.airports.length > 0) {
      setDestination(firstCity.airports[0], 'airport');
    } else {
      setDestination(firstCity, 'city');
    }
  }
}

// Handle Backspace key press for Departure
function handleDepartureBackspace(event) {
  console.log('Departure backspace:', {
    query: departureQuery.value,
    focusedIndex: departureFocusedIndex.value,
    itemsLength: selectedDepartureItems.value.length,
    items: selectedDepartureItems.value
  });
  
  if (departureQuery.value === '') {
    if (departureFocusedIndex.value >= 0) {
      // Remove the focused item
      console.log('Removing focused departure item');
      selectedDepartureItems.value.splice(departureFocusedIndex.value, 1);
      // Adjust focus index
      if (departureFocusedIndex.value >= selectedDepartureItems.value.length) {
        departureFocusedIndex.value = selectedDepartureItems.value.length - 1;
      }
      emit('update-departureCities', [...selectedDepartureItems.value]);
    } else if (selectedDepartureItems.value.length > 0) {
      // Focus on the last item when backspace is pressed with empty input
      console.log('Focusing last departure item');
      departureFocusedIndex.value = selectedDepartureItems.value.length - 1;
      event.preventDefault();
    }
  } else {
    // Reset focus when typing
    departureFocusedIndex.value = -1;
  }
}

// Handle Backspace key press for Destination
function handleDestinationBackspace(event) {
  console.log('Destination backspace:', {
    query: destinationQuery.value,
    focusedIndex: destinationFocusedIndex.value,
    itemsLength: selectedDestinationItems.value.length,
    items: selectedDestinationItems.value
  });
  
  if (destinationQuery.value === '') {
    if (destinationFocusedIndex.value >= 0) {
      // Remove the focused item
      const itemToRemove = selectedDestinationItems.value[destinationFocusedIndex.value];
      console.log('Removing focused item:', itemToRemove);
      deselectFromMap(itemToRemove);
      selectedDestinationItems.value.splice(destinationFocusedIndex.value, 1);
      // Adjust focus index
      if (destinationFocusedIndex.value >= selectedDestinationItems.value.length) {
        destinationFocusedIndex.value = selectedDestinationItems.value.length - 1;
      }
      emit('update-destinationCities', [...selectedDestinationItems.value]);
    } else if (selectedDestinationItems.value.length > 0) {
      // Focus on the last item when backspace is pressed with empty input
      console.log('Focusing last item');
      destinationFocusedIndex.value = selectedDestinationItems.value.length - 1;
      event.preventDefault();
    } else {
      console.log('Hiding Anywhere');
      isVisible.value = false;
    }
  } else {
    // Reset focus when typing
    destinationFocusedIndex.value = -1;
  }
}


// Handle Focus on Departure Input
function handleDepartureFocus() {
  // Close destination dropdown and quick picks
  showDestinationDropdown.value = false;
  showDepartureQuickPick.value = false;
  showDestinationQuickPick.value = false;
  // Reset focused index when input is focused
  departureFocusedIndex.value = -1;

  if (departureSuggestions.value.countries.length > 0 || departureSuggestions.value.cities.length > 0) {
    showDepartureDropdown.value = true;
  }
}

// Handle Focus on Destination Input
function handleDestinationFocus() {
  // Close departure dropdown and quick picks
  showDepartureDropdown.value = false;
  showDepartureQuickPick.value = false;
  showDestinationQuickPick.value = false;
  // Reset focused index when input is focused
  destinationFocusedIndex.value = -1;

  if (destinationSuggestions.value.countries.length > 0 || destinationSuggestions.value.cities.length > 0) {
    showDestinationDropdown.value = true;
  }
}

// Keyboard navigation handlers for Departure
function handleDepartureArrowLeft(event) {
  if (departureQuery.value === '' && selectedDepartureItems.value.length > 0) {
    event.preventDefault();
    if (departureFocusedIndex.value === -1) {
      // Focus on last item
      departureFocusedIndex.value = selectedDepartureItems.value.length - 1;
    } else if (departureFocusedIndex.value > 0) {
      // Move focus left
      departureFocusedIndex.value--;
    }
  }
}

function handleDepartureArrowRight(event) {
  if (departureQuery.value === '' && departureFocusedIndex.value >= 0) {
    event.preventDefault();
    if (departureFocusedIndex.value < selectedDepartureItems.value.length - 1) {
      // Move focus right
      departureFocusedIndex.value++;
    } else {
      // Return focus to input
      departureFocusedIndex.value = -1;
    }
  }
}

function handleDepartureDelete(event) {
  if (departureFocusedIndex.value >= 0) {
    event.preventDefault();
    removeSelectedItem(selectedDepartureItems.value[departureFocusedIndex.value], 'departure');
  }
}

// Keyboard navigation handlers for Destination
function handleDestinationArrowLeft(event) {
  if (destinationQuery.value === '' && selectedDestinationItems.value.length > 0) {
    event.preventDefault();
    if (destinationFocusedIndex.value === -1) {
      // Focus on last item
      destinationFocusedIndex.value = selectedDestinationItems.value.length - 1;
    } else if (destinationFocusedIndex.value > 0) {
      // Move focus left
      destinationFocusedIndex.value--;
    }
  }
}

function handleDestinationArrowRight(event) {
  if (destinationQuery.value === '' && destinationFocusedIndex.value >= 0) {
    event.preventDefault();
    if (destinationFocusedIndex.value < selectedDestinationItems.value.length - 1) {
      // Move focus right
      destinationFocusedIndex.value++;
    } else {
      // Return focus to input
      destinationFocusedIndex.value = -1;
    }
  }
}

function handleDestinationDelete(event) {
  if (destinationFocusedIndex.value >= 0) {
    event.preventDefault();
    removeSelectedItem(selectedDestinationItems.value[destinationFocusedIndex.value], 'destination');
  }
}

// Handle clicks outside the component to hide dropdowns
// Guard: ignore clicks inside teleported map modal (it lives outside componentRef in the DOM)
function handleClickOutside(event) {
  if (isMapVisible.value) return;
  if (componentRef.value && !componentRef.value.contains(event.target)) {
    hideDepartureDropdown();
    hideDestinationDropdown();
    showDepartureQuickPick.value = false;
    showDestinationQuickPick.value = false;
  }
}


onMounted(() => {
  // Register event listener immediately - don't wait for map to load
  // The map component will emit events when countries are selected
  eventBus.on('countries-updated', (selectedItems) => {
    console.log('Received selection:', selectedItems);
    
    // Set flag to prevent prop watcher from overwriting
    isInternalUpdate.value = true;
    
    // Clear previous country/continent selections
    selectedDestinationItems.value = selectedDestinationItems.value.filter(
      item => item.type !== 'country' && item.type !== 'continent' && !isAnywhereItem(item)
    );
    
    // Add each selected item with proper data
    selectedItems.forEach(item => {
      if (item.type === 'continent') {
        // Handle continent selection
        selectedDestinationItems.value.push({
          id: item.id,
          name: item.display_name || item.name,
          display: `All ${item.name}`,
          type: 'continent',
          code: item.id
        });
      } else {
        // Handle individual country selection - try to get country info from map data
        let countryName = item.name;
        if (!countryName && window.simplemaps_worldmap_mapdata?.state_specific?.[item.code]) {
          countryName = window.simplemaps_worldmap_mapdata.state_specific[item.code].name;
        }
        if (countryName) {
          selectedDestinationItems.value.push({
            id: item.code,
            name: countryName,
            display: countryName,
            type: 'country',
            code: item.code
          });
        }
      }
    });

    if (selectedDestinationItems.value.length > 0) isVisible.value = false;

    // Emit updated destinationCities
    emit('update-destinationCities', [...selectedDestinationItems.value]);
    scrollChipStripToEnd(destinationChipStrip.value);
    
    // Reset flag after a short delay to allow prop update to propagate
    setTimeout(() => {
      isInternalUpdate.value = false;
    }, 100);
  });

  // Clean up on unmount
  onUnmounted(() => {
    eventBus.off('countries-updated');
    document.removeEventListener('click', handleClickOutside);
  });

  // Fill the departure field from the detected location, but only when nothing was passed in
  // (deep links, restored searches) and only when the detection is trustworthy.
  const autoFillDeparture = async () => {
    const location = await getAutofillOrigin();
    await nextTick();
    if (!location || selectedDepartureItems.value.length > 0 || props.departureCities?.length) return;

    // Recorded before the field is filled so the fill itself is not read as a correction.
    trackOriginAutofill(location, 'departure-field');
    setDeparture({
      id: location.city.code,
      name: cityName(location.city, locale.value),
      city_code: location.city.code,
      country_code: location.city.country_code || '',
    }, 'city');
  };

  if (!props.skipAutoFill) autoFillDeparture();

  // Add listeners to keep teleported dropdowns aligned with their inputs.
  // capture: true catches scroll events from any scrollable element (not just window).
  window.addEventListener('scroll', updateDropdownPositions, { capture: true, passive: true });
  window.addEventListener('resize', updateDropdownPositions);
});

// Don't forget to clean up
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  eventBus.off('countries-updated');
});

// Map modal ESC key handler
const handleEscKeyPress = (event) => {
  if (event.key === 'Escape' && isMapVisible.value) {
    toggleMapVisibility();
  }
};

onMounted(() => {
  // Add event listener for ESC key
  window.addEventListener('keydown', handleEscKeyPress);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  // Remove event listener when component is unmounted
  window.removeEventListener('keydown', handleEscKeyPress);
  window.removeEventListener('scroll', updateDropdownPositions, { capture: true });
  window.removeEventListener('resize', updateDropdownPositions);
});

// Hide dropdown functions
function hideDepartureDropdown() {
  showDepartureDropdown.value = false;
}

function hideDestinationDropdown() {
  showDestinationDropdown.value = false;
}

// Then fix watchers
watch(
  () => props.departureCities,  // Now properly defined
  (newCities) => {
    console.log('Departure cities updated:', newCities);
    if (newCities) {
      selectedDepartureItems.value = Array.isArray(newCities) ? newCities : [];
    }
  },
  { immediate: true }
);

watch(
  () => props.destinationCities,  // Now properly defined
  (newCities) => {
    console.log('Destination cities updated:', newCities);
    // Skip update if this was triggered by an internal map selection
    if (isInternalUpdate.value) {
      console.log('Skipping prop update - internal change');
      return;
    }
    if (newCities) {
      const incomingCities = Array.isArray(newCities) ? newCities : [];
      const hasAnywhere = incomingCities.some(isAnywhereItem);
      selectedDestinationItems.value = incomingCities.filter(item => !isAnywhereItem(item));
      if (selectedDestinationItems.value.length > 0) isVisible.value = false;
      else if (hasAnywhere) isVisible.value = true;
    }
  },
  { immediate: true }
);

// watch for focusElement prop if = destination -> focus on destination input if = departure -> focus on departure input
onMounted(() => {
  if (props.focusElement === 'destination') {
    destinationRef.value.focus();
  } else if (props.focusElement === 'departure') {
    departureRef.value.focus();
  }
  console.log('Focus element:', props.focusElement);
});
</script>

<style scoped>
/* Transition for dropdowns */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Map bottom-sheet animation */
.map-sheet-enter-active,
.map-sheet-leave-active {
  transition: opacity 0.25s ease;
}
.map-sheet-enter-active .relative,
.map-sheet-leave-active .relative {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.map-sheet-enter-from,
.map-sheet-leave-to {
  opacity: 0;
}
.map-sheet-enter-from .relative,
.map-sheet-leave-to .relative {
  transform: translateY(100%);
}

/* Optional: Style scrollbar for dropdowns */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

.popup-flash {
  animation: darkenBackground 0.3s ease;
}

@keyframes darkenBackground {
  0% {
    background-color: rgba(0, 0, 0, 0.5);
  }
  50% {
    background-color: rgba(0, 0, 0, 0.7);
  }
  100% {
    background-color: rgba(0, 0, 0, 0.5);
  }
}

.locate-button {
  color: rgb(209 213 219); /* text-gray-300 equivalent */
  transition: all 0.3s ease;
}

.locate-button:hover {
  color: rgb(31 41 55); /* text-gray-800 equivalent */
}

.locate-flash {
  animation: flashBackground 2.7s ease;
}

@keyframes flashBackground {
  0% {
    background-color: transparent;
  }
  50% {
    background-color: rgba(0, 0, 0, 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.location-flash {
  animation: emphasizeSetButton 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes emphasizeSetButton {
  0% {
    transform: scale(1);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.locate-button {
  color: rgb(209 213 219);
  transition: all 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.locate-button:hover {
  color: rgb(31 41 55);
}

.locate-flash {
  animation: subtleFlash 0.9s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes subtleFlash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
</style>